import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ChevronRight, Gift, Check, X, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Referral code support (auto-filled from ?ref= query param when arriving
  // from a partner's shareable link). Validated live so the user gets
  // immediate feedback that the perk will apply.
  const [referralCode, setReferralCode] = useState("");
  const [refState, setRefState] = useState({ status: "idle" }); // idle | checking | valid | invalid
  // GDPR + Italian Garante require explicit, granular consent. We track
  // two checkboxes — one mandatory (legal acceptance), one optional
  // (marketing/newsletter, kept for future use; today does nothing).
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auto-fill from URL query (?ref=MARAN-X7K2) on first render
  useEffect(() => {
    const fromUrl = searchParams.get("ref");
    if (fromUrl) setReferralCode(fromUrl.toUpperCase());
  }, [searchParams]);

  // Debounced live validation of the referral code
  useEffect(() => {
    const code = referralCode.trim().toUpperCase();
    if (!code) {
      setRefState({ status: "idle" });
      return;
    }
    setRefState({ status: "checking" });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/referrals/validate/${encodeURIComponent(code)}`);
        if (data?.valid) {
          setRefState({
            status: "valid",
            ownerName: data.owner_name,
            rewardDays: data.reward_days,
            rewardPlan: data.reward_plan,
          });
        } else {
          setRefState({ status: "invalid" });
        }
      } catch (_) {
        setRefState({ status: "invalid" });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [referralCode]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (!legalAccepted) {
      toast.error("Devi accettare Privacy Policy e Termini di Servizio per continuare");
      return;
    }
    setLoading(true);
    try {
      const code = refState.status === "valid" ? referralCode.trim().toUpperCase() : null;
      await register(name, email, password, code);
      if (code) {
        toast.success(`Account creato! ${refState.rewardDays} giorni di Pro gratis attivati 🎁`);
      } else {
        toast.success("Account creato!");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore di registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 mb-10 group w-fit" data-testid="register-back-home">
          <img
            src="/reelio-icon-transparent.png"
            alt=""
            className="h-9 w-auto"
          />
          <span className="font-display font-black text-2xl tracking-tighter text-white">
            Reelio
          </span>
        </Link>

        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-3">
          · Registrazione
        </div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Crea il tuo studio.</h1>
        <p className="text-zinc-400 font-body mb-10">Gratis. 60 secondi. Nessuna carta richiesta.</p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Nome</label>
            <input
              type="text"
              required
              data-testid="register-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Mario Rossi"
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Email</label>
            <input
              type="email"
              required
              data-testid="register-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@brand.com"
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              Password <span className="text-zinc-600 normal-case tracking-normal">(min 6)</span>
            </label>
            <input
              type="password"
              required
              data-testid="register-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
            />
          </div>

          {/* Referral code — optional. Validates live with a tiny status pill. */}
          <div>
            <label className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              <Gift className="w-3 h-3" />
              Codice referral <span className="text-zinc-600 normal-case tracking-normal">(opzionale)</span>
            </label>
            <div className="relative">
              <input
                type="text"
                data-testid="register-referral-input"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="ESEMP-X7K2"
                className="w-full px-4 py-3 pr-11 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body font-mono tracking-wider"
                autoCapitalize="characters"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {refState.status === "checking" && (
                  <Loader2 className="w-4 h-4 text-zinc-500 animate-spin" />
                )}
                {refState.status === "valid" && (
                  <Check className="w-4 h-4 text-green-500" data-testid="register-referral-valid" />
                )}
                {refState.status === "invalid" && (
                  <X className="w-4 h-4 text-red-500" data-testid="register-referral-invalid" />
                )}
              </div>
            </div>
            {refState.status === "valid" && (
              <p className="mt-2 text-[12px] font-body text-green-400 flex items-center gap-1.5" data-testid="register-referral-message">
                <Gift className="w-3 h-3" />
                Invito di <strong>{refState.ownerName}</strong> · Riceverai <strong>{refState.rewardDays} giorni di Pro gratis</strong>
              </p>
            )}
            {refState.status === "invalid" && (
              <p className="mt-2 text-[12px] font-body text-red-400">
                Codice non valido — controlla l'ortografia
              </p>
            )}
          </div>

          {/* GDPR consent — mandatory checkbox for Privacy + Terms */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={legalAccepted}
              onChange={(e) => setLegalAccepted(e.target.checked)}
              data-testid="register-legal-checkbox"
              required
              className="mt-1 w-5 h-5 accent-[#FF4400] shrink-0"
            />
            <span className="text-[12px] font-body text-zinc-400 leading-relaxed">
              Ho letto e accetto la{" "}
              <Link to="/privacy" target="_blank" className="text-[#FF4400] hover:text-[#FF6633] underline">
                Privacy Policy
              </Link>{" "}
              e i{" "}
              <Link to="/terms" target="_blank" className="text-[#FF4400] hover:text-[#FF6633] underline">
                Termini di Servizio
              </Link>
              . Dichiaro di avere almeno 16 anni.
            </span>
          </label>

          <button
            type="submit"
            disabled={loading || !legalAccepted}
            data-testid="register-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-50 disabled:cursor-not-allowed text-white font-display font-bold rounded-sm transition-colors"
          >
            {loading ? "Creazione..." : "Crea account"}
            {!loading && <ChevronRight className="w-4 h-4" />}
          </button>
        </form>

        <p className="mt-8 text-sm text-zinc-500 font-body">
          Hai già un account?{" "}
          <Link to="/login" data-testid="register-link-login" className="text-[#FF4400] hover:text-[#FF6633] font-bold">
            Accedi
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
